import React from 'react';
import { Wrench, FileText, ShoppingCart, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useContent } from '../hooks/useContent';
import Hero from '../components/ui/Hero';
import SEO from '../components/SEO';

const focusBoxes = [
  {
    key: 'service',
    icon: Wrench,
    image: 'https://assets.auxodev.dk/assets/488e7d70-fe21-4956-bd96-e0986a28cf0b/de2194a3-886b-483c-b7be-d0dc86c2555b.jpg',
    href: '/service',
    title: { da: 'Service', en: 'Service', de: 'Service' },
    text: {
      da: 'Fokus på servicering og drift af skinnekørende arbejdsmaskiner.',
      en: 'Focus on servicing and operating rail-bound work machines.',
      de: 'Fokus auf Service und Betrieb von schienengebundenen Arbeitsmaschinen.'
    }
  },
  {
    key: 'technical-advisory',
    icon: FileText,
    image: 'https://assets.auxodev.dk/assets/488e7d70-fe21-4956-bd96-e0986a28cf0b/2cbfb6aa-acdd-4b8c-b04b-54a666316eea.jpg',
    href: '/teknisk-radgivning',
    title: { da: 'Teknisk rådgivning', en: 'Technical advisory', de: 'Technische Beratung' },
    text: {
      da: 'Fokus på rådgivning, teknisk sparring og løsninger i hele processen.',
      en: 'Focus on advisory, technical sparring, and solutions throughout the process.',
      de: 'Fokus auf Beratung, technische Unterstützung und Lösungen im gesamten Prozess.'
    }
  },
  {
    key: 'sales-purchase',
    icon: ShoppingCart,
    image: 'https://assets.auxodev.dk/assets/488e7d70-fe21-4956-bd96-e0986a28cf0b/f5f7e166-179a-4c33-ab45-13604e7bcb43.jpg',
    href: '/salg-og-kob',
    title: { da: 'Salg og køb', en: 'Sales and purchase', de: 'Verkauf und Kauf' },
    text: {
      da: 'Fokus på køb og salg af skinnekørende arbejdsmaskiner og relevant udstyr.',
      en: 'Focus on buying and selling rail-bound work machines and relevant equipment.',
      de: 'Fokus auf den Kauf und Verkauf von schienengebundenen Arbeitsmaschinen und relevanter Ausrüstung.'
    }
  }
];

const Home = () => {
  const { t, getLocalizedPath } = useLanguage();
  const { data: pagesData, loading: pagesLoading } = useContent('pages_content');
  const { data: contactData, loading: contactLoading } = useContent('contact_info');

  const page = pagesData?.find(p => p.identifier === 'home');
  const contact = contactData?.[0];

  const features = [
    {
      da: 'Speciale inden for skinnekørende arbejdsmaskiner / Yellow Fleet',
      en: 'Specialization in rail-bound work machines / Yellow Fleet',
      de: 'Spezialisierung auf schienengebundene Arbeitsmaschinen / Yellow Fleet'
    },
    {
      da: 'Mange års erfaring med drift og vedligeholdelse',
      en: 'Many years of experience in operation and maintenance',
      de: 'Langjährige Erfahrung im Betrieb und in der Wartung'
    },
    {
      da: 'Unik viden og ekspertise i branchen',
      en: 'Unique knowledge and expertise in the industry',
      de: 'Einzigartiges Wissen und Fachkompetenz in der Branche'
    },
    {
      da: 'Stort netværk i hele Europa',
      en: 'Large network throughout Europe',
      de: 'Großes Netzwerk in ganz Europa'
    }
  ];

  if (pagesLoading || contactLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <main>
      <SEO 
        title={t({ da: 'Forside', en: 'Home', de: 'Startseite' })} 
        description={t({ 
          da: 'Professionel service og teknisk rådgivning inden for skinnekørende arbejdsmaskiner og Yellow Fleet.', 
          en: 'Professional service and technical advisory within rail-bound work machines and Yellow Fleet.',
          de: 'Professioneller Service und technische Beratung für schienengebundene Arbeitsmaschinen und Yellow Fleet.' 
        })} 
      />
      
      <Hero
        title={page?.hero_title || { da: 'Rail & Maskinteknik ApS', en: 'Rail & Maskinteknik ApS', de: 'Rail & Maskinteknik ApS' }}
        subtitle={page?.hero_subtitle || { da: 'Professionel service og rådgivning', en: 'Professional service and advisory', de: 'Professioneller Service und Beratung' }}
        image="https://assets.auxodev.dk/assets/488e7d70-fe21-4956-bd96-e0986a28cf0b/4e92d387-0414-4b1e-9104-2e8febd1f545.jpg"
      >
        <Link 
          to={getLocalizedPath('/kontakt')} 
          className="px-8 py-4 bg-[#f59c00] text-white font-bold rounded-lg hover:bg-[#e08e00] transition-all shadow-lg"
        >
          {t({ da: 'Kontakt os', en: 'Contact Us', de: 'Kontaktieren Sie uns' })}
        </Link>
        {contact && (
          <a 
            href={`tel:${contact.phone?.replace(/\s/g, '') || ''}`}
            className="px-8 py-4 bg-transparent text-white font-bold rounded-lg border border-white/30 hover:bg-white/10 transition-all"
          >
            {t({ da: 'Ring på', en: 'Call', de: 'Anrufen' })} {contact.phone}
          </a>
        )}
      </Hero>

      <section className="pb-12 bg-white relative z-20 -mt-20">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative -top-16">
            {focusBoxes.map((box) => {
              const Icon = box.icon;

              return (
                <Link
                  key={box.key}
                  to={getLocalizedPath(box.href)}
                  className="group flex flex-col h-full rounded-2xl border border-slate-100 bg-white shadow-xl shadow-slate-200/50 hover:shadow-2xl hover:shadow-slate-300/50 transition-all duration-500 overflow-hidden"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={box.image}
                      alt={t(box.title)}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/20 to-transparent" />
                    <div className="absolute top-5 left-5">
                      <div className="w-12 h-12 bg-white/95 backdrop-blur rounded-xl flex items-center justify-center shadow-lg">
                        <Icon className="w-5 h-5 text-slate-900" />
                      </div>
                    </div>
                  </div>

                  <div className="p-8 flex flex-col flex-grow">
                    <h3 className="text-2xl font-bold text-slate-900 mb-4">{t(box.title)}</h3>
                    <p className="text-slate-600 mb-8 leading-relaxed flex-grow">{t(box.text)}</p>
                    <span className="inline-flex items-center gap-2 text-sm font-bold text-slate-900 mt-auto">
                      {t({ da: 'Læs mere', en: 'Read more', de: 'Mehr lesen' })}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
      {/* Expertise Section */}
      <section className="py-24 bg-slate-50 overflow-hidden">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="lg:w-1/2">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 tracking-tight">
                {t({ da: 'Ekspertise og erfaring', en: 'Expertise and Experience', de: 'Fachkompetenz und Erfahrung' })}
              </h2>
              <div className="space-y-6">
                {features.map((feature, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-4"
                  >
                    <div className="mt-1 bg-white p-1 rounded-full shadow-sm">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <p className="text-lg text-slate-700 font-medium">{t(feature)}</p>
                  </div>
                ))}
              </div>
              <div className="mt-12 p-8 bg-white rounded-2xl border border-slate-100 shadow-sm italic text-slate-600 text-lg">
                {t({
                  da: '"Har I idéen, så har vi løsningen"',
                  en: '"If you have the idea, we have the solution"',
                  de: '"Haben Sie die Idee, haben wir die Lösung"'
                })}
              </div>
            </div>
            <div className="lg:w-1/2 relative">
              <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://assets.auxodev.dk/assets/488e7d70-fe21-4956-bd96-e0986a28cf0b/4e92d387-0414-4b1e-9104-2e8febd1f545.jpg" 
                  alt="Service work" 
                  className="w-full h-auto"
                />
              </div>
              {/* Technical accent */}
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-slate-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse delay-700"></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-8 tracking-tight">
            {t({ da: 'Klar til at optimere jeres drift?', en: 'Ready to optimize your operation?', de: 'Bereit, Ihren Betrieb zu optimieren?' })}
          </h2>
          <p className="text-xl text-slate-400 mb-12 max-w-2xl mx-auto">
            {t({ 
              da: 'Kontakt os i dag for en professionel vurdering af jeres behov for service eller rådgivning.', 
              en: 'Contact us today for a professional assessment of your service or advisory needs.',
              de: 'Kontaktieren Sie uns noch heute für eine professionelle Bewertung Ihres Service- oder Beratungsbedarfs.' 
            })}
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <Link 
              to={getLocalizedPath('/kontakt')} 
              className="px-10 py-4 bg-[#f59c00] text-white font-bold rounded-lg hover:bg-[#e08e00] transition-all"
            >
              {t({ da: 'Kontakt os nu', en: 'Contact us now', de: 'Jetzt kontaktieren' })}
            </Link>
            {contact && (
              <a 
                href={`mailto:${contact.email}`}
                className="px-10 py-4 bg-transparent text-white border border-white/20 font-bold rounded-lg hover:bg-white/5 transition-all"
              >
                {t({ da: 'Send en mail', en: 'Send an email', de: 'E-Mail senden' })}
              </a>
            )}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
